/**
 * The artist time zone (`AccountModel.timeZone`, Settings → Artist → Time zone). One rule for the
 * API (validation) and the web app (the picker, the "release day" start of a one-off publish).
 * No imports: every project may read it.
 */

/** The shape of an IANA zone name (`Europe/Warsaw`, `America/Argentina/Buenos_Aires`, `UTC`). No offsets. */
const IANA_NAME = /^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+)*$/;

/** The longest zone name we store. */
export const TIME_ZONE_MAX_LENGTH = 64;

/**
 * The zones the picker shows when the runtime has no `Intl.supportedValuesOf` (Safari < 15.4).
 * The common zones of the music markets, one per offset at least.
 */
export const FALLBACK_TIME_ZONES: readonly string[] = [
  'Pacific/Honolulu',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Denver',
  'America/Phoenix',
  'America/Chicago',
  'America/Mexico_City',
  'America/New_York',
  'America/Toronto',
  'America/Bogota',
  'America/Lima',
  'America/Caracas',
  'America/Santiago',
  'America/Argentina/Buenos_Aires',
  'America/Sao_Paulo',
  'Atlantic/Azores',
  'UTC',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Africa/Lagos',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Stockholm',
  'Europe/Warsaw',
  'Africa/Johannesburg',
  'Africa/Cairo',
  'Europe/Athens',
  'Europe/Helsinki',
  'Europe/Tallinn',
  'Europe/Kyiv',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Africa/Nairobi',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Jakarta',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Manila',
  'Asia/Seoul',
  'Asia/Tokyo',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Sydney',
  'Pacific/Auckland',
];

/** True for an IANA zone name that this runtime knows. An offset (`+02:00`) is not a zone name. */
export function isIanaTimeZone(timeZone: unknown): timeZone is string {
  if (typeof timeZone !== 'string' || timeZone.length === 0 || timeZone.length > TIME_ZONE_MAX_LENGTH) {
    return false;
  }
  if (!IANA_NAME.test(timeZone)) {
    return false;
  }
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date(0));
    return true;
  } catch {
    return false;
  }
}

/** Every zone the picker offers: the runtime list (plus `UTC`), else the fallback list. */
export function allTimeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  let zones: string[] = [];
  try {
    zones = typeof intl.supportedValuesOf === 'function' ? intl.supportedValuesOf('timeZone') : [];
  } catch {
    zones = [];
  }
  const list = zones.length > 0 ? zones : [...FALLBACK_TIME_ZONES];
  return list.includes('UTC') ? list : [...list, 'UTC'];
}

/** The zone of this device, or null when the runtime gives none that we accept. */
export function deviceTimeZone(): string | null {
  try {
    const zone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isIanaTimeZone(zone) ? zone : null;
  } catch {
    return null;
  }
}

function zoneParts(ms: number, timeZone: string): Record<string, number> {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(ms));
  const out: Record<string, number> = {};
  parts.forEach((part) => {
    if (part.type !== 'literal') {
      out[part.type] = Number(part.value);
    }
  });
  return out;
}

/** The offset of `timeZone` from UTC at the instant `at`, in minutes (Warsaw in summer: 120). */
export function timeZoneOffsetMinutes(timeZone: string, at: Date): number {
  const ms = at.getTime();
  const p = zoneParts(ms, timeZone);
  const asUtc = Date.UTC(p.year as number, (p.month as number) - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60_000);
}

/** `UTC+2`, `UTC-3:30`, `UTC+5:45`, `UTC` for an offset in minutes. */
export function formatUtcOffset(minutes: number): string {
  if (minutes === 0) {
    return 'UTC';
  }
  const sign = minutes > 0 ? '+' : '-';
  const abs = Math.abs(minutes);
  const hours = Math.floor(abs / 60);
  const rest = abs % 60;
  return `UTC${sign}${hours}${rest === 0 ? '' : `:${String(rest).padStart(2, '0')}`}`;
}

/** `Europe/Warsaw · UTC+2` at the instant `at`. */
export function timeZoneLabel(timeZone: string, at: Date = new Date()): string {
  return `${timeZone} · ${formatUtcOffset(timeZoneOffsetMinutes(timeZone, at))}`;
}

/**
 * The instant of 00:00 of `day` (`YYYY-MM-DD`) in `timeZone`, or null for a day that is not a day.
 * The same two-step rule as the rollout plan (`zonedTimeOn` in music24-api).
 */
export function zonedMidnight(day: string, timeZone: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
    return null;
  }
  const wall = Date.parse(`${day}T00:00:00.000Z`);
  if (Number.isNaN(wall)) {
    return null;
  }
  const first = wall - timeZoneOffsetMinutes(timeZone, new Date(wall)) * 60_000;
  return new Date(wall - timeZoneOffsetMinutes(timeZone, new Date(first)) * 60_000);
}
